import { FC, ReactNode } from 'react';
import { IonAvatar } from '@ionic/react';
import { IFamilyMember } from '../caremap/ICareMap';
import { EditIcon2, UserIcon } from '../theme/Icons';
import { getDisplayedAvatar } from '../caremap/CareMapFactory';
import { useAvatarImage } from '../avatar/useAvatarImage';
import { getAvatarColor, getContrastingTextColor, getInitials, toAvatarSurfaceColor } from '../avatar/avatarInitials';

// One tone behind every state that is not a rendered image: we do not know yet, we are loading,
// and the image failed. Sharing it means moving between those states never flashes a background
// change, and a screenshot of a stuck avatar is distinguishable from a transparent circle.
const NEUTRAL_FILL = 'var(--ion-color-step-100)';

// Foreground for the placeholder figure when it sits on the neutral fill above.
const PLACEHOLDER_TINT = 'var(--ion-color-step-350)';

// Where the picture falls back to when the person has no image of their own. Care-map members
// get an illustration; people get their initials.
export type IAvatarNoImageFallback = 'initials' | 'illustration';

// The photo link lives on IPatient and IHouseholdMember rather than the shared base, so it is
// widened here instead: this renders both, and a member with no photo carries no link.
type IAvatarMember = Partial<IFamilyMember> & { links?: { photo?: { href: string } } };

const AvatarPlaceholder: FC<{ color: string; dim: number }> = ({ color, dim }) => (
    <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color
    }}>
        <UserIcon color="inherit" customSize={dim * 0.55} />
    </div>
);

// Masked because initials come from a name, and session replay records unmasked text.
const AvatarInitials: FC<{ initials: string; background: string; dim: number }> = ({ initials, background, dim }) => (
    <div className="mp-mask" style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: getContrastingTextColor(background),
        fontSize: dim * 0.4,
        fontWeight: 500,
        lineHeight: 1
    }}>
        {initials}
    </div>
);

const AvatarImage: FC<{ url: string }> = ({ url }) => (
    <img
        src={url}
        alt=""
        style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            // TODO: better way to set these up / scale / position them, based on our
            // "profile image" effort in the future
            objectPosition: '75% 40%'
        }}
    />
);

const EditableAvatarFrame: FC<{ onClick: (e: React.MouseEvent) => void; children: ReactNode }> = ({ onClick, children }) => (
    <div style={{ cursor: 'pointer', position: 'relative'}} onClick={onClick}>
        {children}
        <div style={{ position: 'absolute', bottom: -2, right: -2 }}>
            <EditIcon2 size="small" shape="round" backgroundColor="white" borderColor="medium" />
        </div>
    </div>
);

/**
 * Works out what the circle shows before any loading is considered: which picture to try, and what
 * to fall back to if there is none. A resolved person always gets a colour, whether or not we can
 * spell them -- initials when we have a name, the placeholder figure when we do not. That colour
 * is what separates "this is someone we know" from a failed image, which keeps the neutral fill.
 */
const resolveAvatarPresentation = ({ member, name, noImageFallback }: {
    member?: IAvatarMember;
    name?: string;
    noImageFallback: IAvatarNoImageFallback;
}) => {
    const initials = getInitials(name);
    const usesInitials = noImageFallback === 'initials';

    // Only the photo drawn here counts: a pending photoDataURI is not rendered by this component.
    const illustration = member && (usesInitials
        ? member.avatar
        : getDisplayedAvatar({ photo: member.photo, avatar: member.avatar, color: member.color }));
    const hasImage = !!member && (!!member.photo || !!illustration);

    const identityBackground = toAvatarSurfaceColor(member?.color || getAvatarColor(name));
    const showIdentity = !hasImage && !!member && usesInitials;
    const colorOnly = !hasImage && !usesInitials ? member?.color : undefined;

    return { illustration, hasImage, initials, identityBackground, showIdentity, colorOnly };
};

// Tests read the chosen visual from data-avatar. Initials go out as a token rather than the letters,
// because session replay records attribute values even inside mp-mask.
const describeVisual = ({ url, showIdentity, initials, color }: { url?: string; showIdentity: boolean; initials: string; color?: string }): string | undefined => {
    if (url) {
        return url;
    }
    if (showIdentity) {
        return initials ? 'initials' : 'placeholder';
    }
    return color;
};

export const MemberAvatar: FC<{
    // Undefined means the caller has not loaded this person yet: a plain neutral circle, so no
    // fallback appears before the real picture.
    member?: IAvatarMember;
    name?: string;
    noImageFallback?: IAvatarNoImageFallback;
    small?: boolean;
    xsmall?: boolean;
    // explicit pixel diameter; overrides the small/xsmall presets when set
    size?: number;
    onClick?: (e: React.MouseEvent) => void;
}> = ({
    member,
    name,
    noImageFallback = 'illustration',
    small = false,
    xsmall = false,
    size: sizeOverride,
    onClick
}) => {
    const { illustration, hasImage, initials, identityBackground, showIdentity, colorOnly } =
        resolveAvatarPresentation({ member, name, noImageFallback });

    const { url, status } = useAvatarImage({
        photo: member?.photo,
        photoHref: member?.links?.photo?.href,
        illustration
    });

    const dim = sizeOverride ?? (xsmall ? 32 : small ? 42 : 64);

    // A loaded image paints its own background. The illustrations' washes are translucent and drawn
    // to sit on white, so the neutral fill behind them would grey them.
    const imageLoaded = !!url && status === 'loaded';
    const identityOrColor = showIdentity ? identityBackground : colorOnly;
    const backgroundColor = imageLoaded ? undefined : (identityOrColor || NEUTRAL_FILL);

    const avatar = (
        <IonAvatar
            style={{ width: dim, height: dim, backgroundColor, overflow: 'hidden' }}
            data-avatar={describeVisual({ url, showIdentity, initials, color: member?.color })}
            data-avatar-status={hasImage ? status : 'none'}
        >
            {url && imageLoaded && <AvatarImage url={url} />}

            {hasImage && status === 'failed' && <AvatarPlaceholder color={PLACEHOLDER_TINT} dim={dim} />}

            {showIdentity && initials && (
                <AvatarInitials initials={initials} background={identityBackground} dim={dim} />
            )}

            {showIdentity && !initials && (
                <AvatarPlaceholder color={getContrastingTextColor(identityBackground)} dim={dim} />
            )}
        </IonAvatar>
    );

    return onClick ? <EditableAvatarFrame onClick={onClick}>{avatar}</EditableAvatarFrame> : avatar;
};
